import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useTheme } from 'styled-components';

import { unmask } from '~/common/utils/unmask';
import { maskCpf } from '~/common/utils/mask-cpf';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { getCpfValidationError } from '~/common/utils/validate-cpf';

import { FiscalInfoLabel, ModalContent } from './styles';
import { TextInput } from '../TextInput';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Typography } from '../Typograph';

interface UserDocumentModalProps {
  open: boolean;
  close: () => void;
  onSubmit: (document: string) => Promise<void>;
}
export const UserDocumentModal: FC<UserDocumentModalProps> = ({
  open,
  close,
  onSubmit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [loading, setLoading] = useState(false);
  const [document, setDocument] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDocument('');
      setErrorMessage(null);
      setLoading(false);
    }
  }, [open]);

  const handleChangeDocument: ChangeEventHandler<HTMLInputElement> =
    useCallback((ev) => {
      setDocument(unmask(ev.target.value));
      setErrorMessage(null);
    }, []);

  const handleSubmit: FormEventHandler = useCallback(
    async (event) => {
      event.preventDefault();

      const validationError = getCpfValidationError(document);

      if (validationError) {
        setErrorMessage(validationError);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      onSubmit(document)
        .catch((error: Error) => {
          setErrorMessage(
            error?.message || 'Nao foi possivel validar o CPF informado.'
          );
        })
        .finally(() => {
        setLoading(false);
        });
    },
    [document, onSubmit]
  );

  return (
    <Modal
      id="user-document-modal"
      isOpen={open}
      onClose={close}
      width="500px"
      title="Por favor, informe seu documento"
      height={isMobile ? '100%' : 'auto'}
      closeOnOutsideClick={false}
      hasCloseButton={false}
    >
      <ModalContent onSubmit={handleSubmit}>
        <FiscalInfoLabel>
          <Typography.Paragraph size="small" color="neutral300">
            Precisamos do seu CPF para a emissao de notas fiscais das suas
            assinaturas e cobrancas.
          </Typography.Paragraph>
        </FiscalInfoLabel>

        <TextInput
          name="title"
          width="100%"
          value={maskCpf(document || '')}
          onChange={handleChangeDocument}
          placeholder="Informe seu CPF"
          backgroundColor="neutral900"
          onHoverBackgroundColor="neutral800"
          onFocusBorderColor="neutral400"
          borderColor="neutral800"
          label={{
            label: 'CPF',
            isRequired: true,
          }}
          error={errorMessage || undefined}
          helperText="Digite os 11 numeros do CPF."
        />

        <Button
          text="Enviar"
          type="submit"
          backgroundColor={theme.primary}
          textOrIconColor={theme.neutral1000}
          borderColor={theme.primary}
          width="100%"
          justifyContent="center"
          loading={loading}
          loader="bar"
          disabled={loading}
        />
      </ModalContent>
    </Modal>
  );
};
